<h2 mat-dialog-title>Grabaciones del acta</h2>

<mat-dialog-content>
  @if (recording_items.length === 0) {
    <p class="empty-message">No hay grabaciones vinculadas a esta acta.</p>
  } @else {
    @for (item of recording_items; track item.recording.id) {
      <div class="recording-item">
        <div class="recording-item-header">
          <div class="recording-item-title">
            <p>
              <strong>{{ item.recording.concept }}</strong>
            </p>
            <p class="recording-meta">
              {{ item.recording | mediaRecordingStatusLabel }}
              @if (item.recording.duration_seconds !== null && item.recording.duration_seconds !== undefined) {
                · {{ item.recording.duration_seconds | recordingElapsedLabel }}
              }
            </p>
          </div>
          @if (item.can_delete) {
            <button
              mat-icon-button
              type="button"
              class="recording-delete-button"
              matTooltip="Eliminar grabación"
              aria-label="Eliminar grabación"
              [disabled]="deleting_recording_id === item.recording.id"
              (click)="deleteRecording(item)"
            >
              @if (deleting_recording_id === item.recording.id) {
                <mat-spinner diameter="20"></mat-spinner>
              } @else {
                <mat-icon>delete</mat-icon>
              }
            </button>
          }
        </div>

        @if (item.recording | mediaRecordingProcessingOnServer) {
          <div class="recording-banner recording-banner-pending">
            <div class="recording-transcription-status-row">
              <mat-spinner diameter="20"></mat-spinner>
              <p>
                <strong>{{ item.recording | mediaRecordingStatusLabel }}</strong> —
                {{ item.recording | mediaRecordingServerProcessingLabel }}
              </p>
            </div>
          </div>
        }

        @if (item.upload_retry_context) {
          <div class="recording-banner recording-banner-error">
            <p>
              <strong>{{ item.upload_retry_context.failure_summary }}</strong>
            </p>
            @if (item.upload_retry_context.retry_summary) {
              <p class="recording-meta">{{ item.upload_retry_context.retry_summary }}</p>
            }
            @if (item.upload_retry_context.can_retry) {
              <button
                mat-stroked-button
                type="button"
                [disabled]="retrying_upload_recording_id === item.recording.id"
                (click)="retryRecordingUpload(item)"
              >
                @if (retrying_upload_recording_id === item.recording.id) {
                  Reintentando…
                } @else {
                  {{ item.upload_retry_context.button_label }}
                }
              </button>
            }
            @if (item.can_discard_local_upload) {
              <button
                mat-stroked-button
                type="button"
                [disabled]="discarding_upload_recording_id === item.recording.id"
                (click)="discardFailedRecordingUpload(item)"
              >
                @if (discarding_upload_recording_id === item.recording.id) {
                  Descartando…
                } @else {
                  Descartar
                }
              </button>
            }
          </div>
        }

        @if (item.supports_youtube_playback && item.youtube_embed_url) {
          <div class="recording-player recording-player-youtube">
            <iframe
              [src]="item.youtube_embed_url | safeUrl"
              title="Reproducción en YouTube"
              referrerpolicy="strict-origin-when-cross-origin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
            ></iframe>
          </div>
          @if (item.youtube_page_url) {
            <a class="recording-external-link" [href]="item.youtube_page_url" target="_blank" rel="noopener">Abrir en YouTube</a>
          }
        }

        @if (item.supports_local_playback) {
          @if (item.is_loading_playback) {
            <div class="recording-player-loading">
              <mat-spinner diameter="28"></mat-spinner>
              <span>Cargando reproducción…</span>
            </div>
          } @else if (item.playback_error) {
            <p class="recording-playback-error">{{ item.playback_error }}</p>
          } @else if (item.playback_object_url) {
            @if (item.local_playback_kind === 'audio') {
              <audio class="recording-player recording-player-audio" controls [src]="item.playback_object_url"></audio>
            }
            @if (item.local_playback_kind === 'video') {
              <video class="recording-player recording-player-video" controls [src]="item.playback_object_url"></video>
            }
          }
        }

        @if (item.recording.recording_status === 'completed') {
          @if (item.recording | mediaRecordingTranscriptionInFlight) {
            <div class="recording-banner recording-banner-pending">
              <div class="recording-transcription-status-row">
                <mat-spinner diameter="20"></mat-spinner>
                <p>
                  <strong>Transcripción {{ item.recording.transcription_status | mediaRecordingTranscriptionStatusLabel }}</strong>
                  @if (item.recording.transcription_status === 'processing') {
                    — transcribiendo audio…
                  } @else {
                    — en cola de procesamiento
                  }
                </p>
              </div>
            </div>
          }

          @if (item.recording.transcription_status === 'failed') {
            <div class="recording-banner recording-banner-error">
              <p><strong>Error de transcripción</strong></p>
              <p class="recording-meta">{{ item.recording.transcription_error ?? 'No se pudo completar la transcripción.' }}</p>
            </div>
          }

          <div class="recording-actions-row">
            @if (item.recording.media_type === 'audio') {
              <button mat-stroked-button type="button" (click)="downloadRecordingAudio(item.recording.id)">Descargar audio</button>
            }
            @if (item.show_retry_transcription_action) {
              <button mat-stroked-button type="button" (click)="retryTranscription(item.recording.id)">
                @if (item.recording.transcription_status === 'failed' || item.recording.transcription_status === 'processing') {
                  Reintentar transcripción
                } @else {
                  Generar transcripción
                }
              </button>
            }
            @if (item.show_diarization_actions) {
              <button mat-stroked-button type="button" (click)="openDiarizationEditor(item.recording.id)">Editar diarización</button>
            }
          </div>

          @if (
            item.recording.transcription_status !== null &&
            !(item.recording | mediaRecordingTranscriptionInFlight) &&
            item.recording.transcription_status !== 'failed'
          ) {
            <p class="recording-meta">Transcripción: {{ item.recording.transcription_status | mediaRecordingTranscriptionStatusLabel }}</p>
          }
        }
      </div>
    }
  }
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-stroked-button type="button" (click)="close()">Cerrar</button>
</mat-dialog-actions>
